import { useEffect, useState, useCallback, lazy, Suspense } from 'react'
import Turnstile from 'react-turnstile'
import {
  getUrlParams,
  sendToParent,
  TURNSTILE_SITE_KEY,
  getThemeClasses,
  fetchEmbedToken
} from './types'

const FullApp = lazy(() => import('./FullApp').then(m => ({ default: m.EmbedApp })))

export function EmbedApp() {
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const urlParams = getUrlParams()
  const { clientId, theme, workspaceId } = urlParams

  const buildMode = (import.meta as any).env.VITE_EMBED_MODE;
  const mode = urlParams.mode || buildMode || 'token';

  const baseUrl = urlParams.baseUrl || import.meta.env.VITE_API_URL || 'https://api.distri.dev/v1'

  const handleTurnstileVerify = useCallback((token: string) => {
    console.log('[EmbedApp] Turnstile verified');
    setTurnstileToken(token)
  }, [])

  const handleTurnstileError = useCallback((e: any) => {
    const msg = `Verification failed. ${e}.`;
    console.error('[EmbedApp] Turnstile error:', e);
    setError(msg)
    setLoading(false)
    sendToParent({ type: 'distri:error', error: msg })
  }, [])

  // Auto-fetch token when Turnstile verification is ready
  useEffect(() => {
    if (!turnstileToken || !clientId) return

    console.log('[EmbedApp] Fetching token with Turnstile verification...', { baseUrl, clientId });
    setLoading(true)
    setError(null)

    fetchEmbedToken(baseUrl, clientId, turnstileToken, workspaceId)
      .then(({ token }) => {
        console.log('[EmbedApp] Token obtained successfully');
        setToken(token)
        setLoading(false)
        sendToParent({ type: 'distri:token', token })
      })
      .catch((err) => {
        const errMsg = err instanceof Error ? err.message : String(err);
        console.error('[EmbedApp] Token fetch failed:', errMsg);
        setError(errMsg)
        setLoading(false)
        sendToParent({ type: 'distri:error', error: errMsg })
      })
  }, [turnstileToken, clientId, baseUrl, workspaceId])

  // Handle refresh requests from parent
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'distri:refresh_token') {
        console.log('[EmbedApp] Refresh requested - resetting Turnstile');
        setToken(null);
        setTurnstileToken(null);
        setLoading(false);
        setError(null);
        setRefreshKey(prev => prev + 1);
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // If in full mode, we render the heavy UI
  if (mode === 'full') {
    return (
      <Suspense fallback={
        <div className={`h-screen flex items-center justify-center bg-background ${getThemeClasses(theme)}`}>
          <div className="flex flex-col items-center gap-2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-[10px] text-muted-foreground animate-pulse font-sans">Loading Chat UI...</p>
          </div>
        </div>
      }>
        <FullApp />
      </Suspense>
    )
  }

  // Token provider UI (minimal)
  return (
    <div className={`embed-token-provider h-full min-h-screen bg-transparent ${getThemeClasses(theme)}`}>
      <div className="flex flex-col items-center justify-center h-full min-h-[100px] p-2 overflow-hidden">
        {(!token || loading) && !error && (
          <div className="flex flex-col items-center gap-2">
            {!turnstileToken && (
              <Turnstile
                key={refreshKey}
                sitekey={TURNSTILE_SITE_KEY}
                onVerify={handleTurnstileVerify}
                onError={handleTurnstileError}
                theme={theme === 'dark' ? 'dark' : 'light'}
              />
            )}
            {loading && <p className="text-[10px] text-muted-foreground animate-pulse font-sans">Verifying...</p>}
          </div>
        )}
        {error && (
          <div className="flex flex-col items-center gap-1">
            <p className="text-[10px] text-destructive font-sans text-center font-semibold">Auth Error</p>
            <p className="text-[9px] text-destructive font-sans text-center opacity-80">{error}</p>
          </div>
        )}
        {token && !loading && (
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
            <p className="text-[10px] text-muted-foreground font-sans uppercase tracking-wider font-semibold opacity-70">Authenticated</p>
          </div>
        )}
      </div>
    </div>
  )
}
